package com.taocent.simple.compose.component.richtext.core.internal.ui

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.platform.rememberKeyboardHeight
import com.taocent.simple.compose.component.richtext.core.internal.platform.rememberKeyboardNeedsInsets

/**
 * 工具栏 / 面板的高度与状态机 — :richtext 与 :blockrichtext 共用。
 *
 * 取代 [RichTextEditor] 中 `RichTextEditorContainer` 与 [BlockRichTextEditor] 中
 * `BlockRichTextEditorContent` 各自实现的:
 * - `rememberedKeyboardHeight` 记忆(键盘隐藏后仍保持面板高度稳定,避免 `Surface` 跳变);
 * - `collapseKeyboardPanel` / `keyboardReplacingPanel` 状态;
 * - `panelTargetHeight` 三段式分支(折叠 / 替换键盘 / 展开 / 仅键盘);
 * - `animateDpAsState` 弹簧动画(与项目 220ms / DampingRatioNoBouncy / StiffnessMediumLow 一致);
 * - 键盘出现时强制 `clearCategory`/`activeCategory = null`。
 *
 * 用法:
 * ```
 * val panelController = rememberEditorPanelController(config.panel.defaultHeight)
 * // 在工具栏按钮的 onClick 中:
 * //   panelController.requestExpand()    — 打开面板
 * //   panelController.requestCollapse()  — 折叠面板
 * //   panelController.requestKeyboardCollapse() / requestKeyboardExpand() — 键盘按钮
 * Column { ...; EditorScaffold(panelController, state) { toolbar() / panel(it) } }
 * ```
 */
@Stable
class EditorPanelController internal constructor(
    private val defaultPanelHeight: Dp,
) {
    /** 键盘可见期间记录的最大高度 — 避免键盘隐藏后面板高度跳变 */
    private var rememberedKeyboardHeightDp by mutableStateOf(defaultPanelHeight)

    /** 当前键盘是否可见(由 [rememberEditorPanelController] 每帧刷新) */
    var isKeyboardVisible: Boolean by mutableStateOf(false)
        private set

    /** 强制折叠面板(键盘"下箭头"按钮触发) */
    var collapsePanel: Boolean by mutableStateOf(false)
        private set

    /** 键盘正在替代面板(从键盘切到面板的过渡) */
    var replacingPanelWithKeyboard: Boolean by mutableStateOf(false)
        private set

    /** 当前键盘高度(由 [rememberEditorPanelController] 每帧刷新) */
    private var keyboardHeightDp: Dp by mutableStateOf(0.dp)

    /**
     * 键盘出现时自动调整 [rememberedKeyboardHeightDp],并取消 collapse / replacing 状态,
     * 同时清空外部 category(由调用方注入 [onKeyboardShown] 钩子执行)。
     */
    internal fun onKeyboardVisibilityChanged(
        isVisible: Boolean,
        height: Dp,
        keyboardSpacing: Dp,
        onKeyboardShown: () -> Unit,
    ) {
        isKeyboardVisible = isVisible
        keyboardHeightDp = keyboardSpacing
        if (isVisible) {
            collapsePanel = false
            replacingPanelWithKeyboard = false
            if (height > rememberedKeyboardHeightDp) {
                rememberedKeyboardHeightDp = height
            }
            onKeyboardShown()
        }
    }

    fun requestExpand() {
        collapsePanel = false
        replacingPanelWithKeyboard = false
    }

    fun requestCollapse() {
        collapsePanel = true
    }

    fun requestKeyboardCollapse() {
        collapsePanel = true
    }

    fun requestKeyboardExpand(hasActiveCategory: Boolean) {
        collapsePanel = false
        replacingPanelWithKeyboard = hasActiveCategory
    }

    /**
     * 计算 panel 当前应展示的目标高度。
     *
     * - `collapsePanel = true` → 0(用户主动折叠 / 键盘按钮"下箭头")
     * - `replacingPanelWithKeyboard = true` → rememberedKeyboardHeight(键盘切面板过渡)
     * - `activeCategory != null` → rememberedKeyboardHeight(有面板打开)
     * - `isKeyboardVisible` → keyboardHeightDp(键盘未切面板时)
     * - else → 0
     */
    @Composable
    fun animatedPanelHeight(hasActiveCategory: Boolean): Dp {
        if (!hasActiveCategory && replacingPanelWithKeyboard) {
            replacingPanelWithKeyboard = false
        }
        val target = when {
            collapsePanel -> 0.dp
            replacingPanelWithKeyboard -> rememberedKeyboardHeightDp
            hasActiveCategory -> rememberedKeyboardHeightDp
            isKeyboardVisible -> keyboardHeightDp
            else -> 0.dp
        }
        val animated by animateDpAsState(
            targetValue = target,
            animationSpec = spring(
                dampingRatio = Spring.DampingRatioNoBouncy,
                stiffness = Spring.StiffnessMediumLow,
            ),
        )
        return animated
    }
}

@Composable
fun rememberEditorPanelController(
    defaultPanelHeight: Dp,
): EditorPanelController {
    val controller = remember(defaultPanelHeight) { EditorPanelController(defaultPanelHeight) }
    val keyboardHeightDp = rememberKeyboardHeight()
    val needsInsets = rememberKeyboardNeedsInsets()
    LaunchedEffect(keyboardHeightDp) {
        val visible = keyboardHeightDp > 0.dp
        controller.onKeyboardVisibilityChanged(
            isVisible = visible,
            height = keyboardHeightDp,
            keyboardSpacing = if (needsInsets) keyboardHeightDp else 0.dp,
            onKeyboardShown = { },
        )
    }
    return controller
}

/**
 * 工具栏 + 面板 组合容器 — :richtext 与 :blockrichtext 共用。
 *
 * 提供:
 * - 顶部 [HorizontalDivider](M3 outline-variant) 与 [MaterialTheme] 配色;
 * - 在 Panel 容器上挂载 `pointerInput { awaitPointerEventScope }`,统一在用户主动
 *   点击面板区时调传入的 [onUserInteraction] 回调(通常是
 *   [com.taocent.simple.compose.component.richtext.core.RichTextState.saveSelection] /
 *   `BlockState.saveSelection`),避免在 toolbar 中重复实现;
 * - 调用方通过 [toolbar] / [panel] 两个 slot 提供内容;[panel] 接收
 *   [EditorPanelController] 自行决定当前是否 `hasActiveCategory`,从而计算出正确高度。
 *
 * 注意:本 Composable 不提供键盘切换按钮 —— 不同编辑器(普通 vs 块级)对键盘按钮
 * 的位置 / 行为略有差异,留调用方在 toolbar slot 内自由实现,统一从
 * [EditorPanelController] 调用 requestKeyboardCollapse / requestKeyboardExpand 即可。
 *
 * KMP 可见性说明:[onUserInteraction] 接受普通 lambda 而非 [RichTextState] 本体,
 * 以便 :blockrichtext 的 [com.taocent.simple.compose.component.blockrichtext.BlockState]
 * (它也提供 [com.taocent.simple.compose.component.blockrichtext.BlockState.saveSelection])
 * 能复用本容器,无须把 core 的 internal 泄漏给业务模块。
 */
@Composable
fun EditorScaffold(
    onUserInteraction: () -> Unit,
    panelController: EditorPanelController,
    modifier: Modifier = Modifier,
    toolbar: @Composable () -> Unit,
    panel: @Composable ColumnScope.(controller: EditorPanelController) -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent(PointerEventPass.Initial)
                        if (event.type == PointerEventType.Press) {
                            onUserInteraction()
                        }
                    }
                }
            },
    ) {
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        toolbar()
        panel(panelController)
    }
}
